import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import useFormError from 'hooks/shared/useFormError';
import FormNumberInput from 'presentation/components/shared/form/FormNumberInput';
import FullScreenLoader from 'presentation/components/shared/FullScreenLoader';
import { formatSelfIpoMoneyAmount } from 'presentation/components/modules/selfIpo/selfIpoUtils';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useApplySelfIpoMutation } from 'rtkQuery/sharespro/queries/selfIpo';
import * as Yup from 'yup';

interface Props {
  offering: SelfIpoOffering;
  portfolio: SelfIpoPortfolio | null | undefined;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  amount: string;
}

const ApplySelfIpoModal: FC<Props> = ({
  offering,
  portfolio,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const handleFormError = useFormError<FormValues>();
  const [applySelfIpo, { isLoading }] = useApplySelfIpoMutation();

  const validationSchema = Yup.object().shape({
    amount: Yup.number()
      .typeError(t('fieldIsRequired') ?? '')
      .required(t('fieldIsRequired') ?? '')
      .min(
        offering.minAmount,
        t('selfIpoMinAmountValidation', {
          amount: formatSelfIpoMoneyAmount(offering.minAmount),
        }) ?? ''
      )
      .test('cash-balance', t('selfIpoAmountExceedsAllowed') ?? '', (value) => {
        return (
          typeof value !== 'number' ||
          !portfolio ||
          value <= portfolio.cashBalance
        );
      }),
  });

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('selfIpoTitle')}</ModalHeader>
          <ModalCloseButton />
          <Formik<FormValues>
            initialValues={{ amount: '' }}
            validationSchema={validationSchema}
            onSubmit={(values, formik) => {
              const amount = Number(values.amount.replace(',', '.'));

              applySelfIpo({
                offering_id: offering.id,
                amount,
              })
                .unwrap()
                .then(() => {
                  toast({
                    description: t('selfIpoApplySuccess'),
                    status: 'success',
                    position: 'top-right',
                    isClosable: true,
                  });
                  onClose();
                  onSuccess();
                  formik.resetForm();
                })
                .catch((err) => {
                  handleFormError(err, formik);
                });
            }}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <ModalBody>
                  <FormNumberInput
                    name="amount"
                    value={values.amount}
                    precision={2}
                    min={0}
                    label={t('selfIpoApplyAmountLabel')}
                    helperText={
                      portfolio
                        ? t('selfIpoCashBalanceHelper', {
                            amount: formatSelfIpoMoneyAmount(
                              portfolio.cashBalance
                            ),
                          })
                        : undefined
                    }
                    onChange={(value) => {
                      setFieldValue('amount', value.replace(',', '.'));
                    }}
                  />
                </ModalBody>
                <ModalFooter>
                  <Button mr={3} size="sm" variant="outline" onClick={onClose}>
                    {t('back')}
                  </Button>
                  <Button size="sm" type="submit">
                    {t('selfIpoApplyAction')}
                  </Button>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        </ModalContent>
      </Modal>
      <FullScreenLoader isLoading={isLoading} />
    </>
  );
};

export default ApplySelfIpoModal;
